import React from "react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

// "Continue with X" — kicks off the X OAuth 2.0 PKCE flow as an
// ANONYMOUS user (no JWT required), distinct from the Producer-only
// "Connect to X" flow in XFollowPanel which calls /api/x/connect/start.
//
// Flow:
//   1. POST /api/x/signin/start → { auth_url, state } (PKCE verifier
//      stashed server-side, keyed by state with sentinel anon user id)
//   2. Open auth_url in the SAME tab (full-page redirect — matches
//      Google's pattern in GoogleAuthButton and works without popups
//      under the iOS Capacitor WKWebView, where window.open is unreliable)
//   3. X redirects to /auth/x/callback with ?code=… &state=…
//   4. The XAuthCallback page reads localStorage's pending attribution
//      payload + POSTs /signin/callback → JWT + maybe pending_username
//
// We deliberately persist any utm_source / utm_campaign / list_slug
// that's currently on the URL into localStorage BEFORE redirecting,
// because X strips them and the callback page wouldn't otherwise know
// the user came from a tracked X invite link.
const XAuthButton = ({ label = "Continue with X", className = "" }) => {
  const [pending, setPending] = React.useState(false);

  const handleClick = async () => {
    setPending(true);
    try {
      // Snapshot landing-attribution so the callback can post it
      // inline with the code exchange. Reads from URL first (fresh
      // visit), falls back to whatever the landing-page beacon
      // already stored.
      const sp = new URLSearchParams(window.location.search);
      const inflightAttr = {
        utm_source: sp.get("utm_source") || localStorage.getItem("ge.x_utm_source") || "",
        utm_campaign: sp.get("utm_campaign") || localStorage.getItem("ge.x_utm_campaign") || "",
        list_slug: sp.get("list") || localStorage.getItem("ge.x_list_slug") || "",
        attribution_id: localStorage.getItem("ge.x_attribution_id") || "",
      };
      localStorage.setItem("ge.x_signin_inflight", JSON.stringify(inflightAttr));
      localStorage.setItem("ge.x_signin_intent", "signin");

      const res = await api.post("/x/signin/start", {});
      const authUrl = res.data?.auth_url;
      const state = res.data?.state;
      if (!authUrl || !state) {
        throw new Error("malformed start response");
      }
      localStorage.setItem("ge.x_signin_state", state);
      window.location.href = authUrl;
    } catch (err) {
      console.error("X sign-in start failed:", err);
      const msg = err?.response?.data?.detail || err?.message || "Couldn't start X sign-in";
      toast.error(msg);
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={pending}
      className={
        `w-full flex items-center justify-center gap-2.5 bg-black hover:bg-gray-900 text-white font-medium py-2.5 rounded-md transition-colors border border-gray-700 disabled:opacity-60 ` + className
      }
      data-testid="continue-with-x-btn"
      aria-label={label}
    >
      <XLogo />
      <span className="text-sm">{pending ? "Connecting to X…" : label}</span>
    </button>
  );
};

const XLogo = () => (
  <svg width="14" height="14" viewBox="0 0 1200 1227" aria-hidden="true">
    <path fill="currentColor" d="M714.163 519.284 1160.89 0h-105.86L667.137 450.887 357.328 0H0l468.492 681.821L0 1226.37h105.866l409.625-476.152 327.181 476.152H1200L714.137 519.284h.026ZM569.165 687.828l-47.468-67.894L144.011 79.694h162.604l304.797 435.991 47.468 67.894 396.2 566.721H892.476L569.165 687.854v-.026Z"/>
  </svg>
);

export default XAuthButton;
